import { create } from 'zustand';
import { api } from '../services/api';
import type { Conversation, Message } from '../types';

interface ChatState {
  conversations: Conversation[];
  currentConversationId: string | null;
  messages: Message[];
  isLoading: boolean;
  isStreaming: boolean;
  error: string | null;

  fetchConversations: () => Promise<void>;
  createConversation: (title?: string) => Promise<string>;
  selectConversation: (id: string) => Promise<void>;
  sendMessage: (content: string, stream?: boolean) => Promise<void>;
  addMessageOptimistic: (message: Message) => void;
  clearError: () => void;
}

export const useChatStore = create<ChatState>((set, get) => ({
  conversations: [],
  currentConversationId: null,
  messages: [],
  isLoading: false,
  isStreaming: false,
  error: null,

  fetchConversations: async () => {
    set({ isLoading: true, error: null });
    try {
      const conversations = await api.get<Conversation[]>('/api/conversations');
      set({ conversations, isLoading: false });
    } catch (e) {
      set({ error: (e as Error).message, isLoading: false });
    }
  },

  createConversation: async (title) => {
    set({ error: null });
    try {
      const conversation = await api.post<Conversation>('/api/conversations', { title });
      set(state => ({ conversations: [conversation, ...state.conversations] }));
      return conversation.id;
    } catch (e) {
      set({ error: (e as Error).message });
      throw e;
    }
  },

  selectConversation: async (id) => {
    set({ currentConversationId: id, isLoading: true, error: null, messages: [] });
    try {
      const messages = await api.get<Message[]>(`/api/conversations/${id}/messages`);
      set({ messages: messages.reverse(), isLoading: false });
    } catch (e) {
      set({ error: (e as Error).message, isLoading: false });
    }
  },

  sendMessage: async (content, stream = true) => {
    const { currentConversationId, addMessageOptimistic } = get();
    if (!currentConversationId) return;
    const requestId = crypto.randomUUID();
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const userMessage: Message = {
      id: `temp-${Date.now()}`,
      conversationId: currentConversationId,
      role: 'user',
      content,
      createdAt: Date.now(),
    };
    addMessageOptimistic(userMessage);

    set({ isStreaming: true, error: null });
    let pendingAssistantId: string | undefined;

    try {
      if (stream) {
        let fullContent = '';
        let assistantMessageId = `temp-${Date.now() + 1}`;
        pendingAssistantId = assistantMessageId;
        let assistantMessage: Message = {
          id: assistantMessageId,
          conversationId: currentConversationId,
          role: 'assistant',
          content: '',
          createdAt: Date.now(),
        };
        addMessageOptimistic(assistantMessage);

        await api.stream(
          '/api/chat',
          { conversationId: currentConversationId, message: content, requestId, timeZone, stream: true },
          (chunk) => {
            fullContent += chunk;
            assistantMessage = { ...assistantMessage, content: fullContent };
            set(state => ({
              messages: state.messages.map(m => m.id === assistantMessageId ? assistantMessage : m),
            }));
          }
        );
      } else {
        const response = await api.post<{ content: string }>('/api/chat', {
          conversationId: currentConversationId,
          message: content,
          requestId,
          timeZone,
          stream: false,
        });
        const assistantMessage: Message = {
          id: `temp-${Date.now() + 1}`,
          conversationId: currentConversationId,
          role: 'assistant',
          content: response.content,
          createdAt: Date.now(),
        };
        addMessageOptimistic(assistantMessage);
      }
    } catch (e) {
      set(state => ({
        error: (e as Error).message,
        messages: state.messages.filter(m => m.id !== pendingAssistantId || m.content !== ''),
      }));
    } finally {
      set({ isStreaming: false });
    }
  },

  addMessageOptimistic: (message) => set(state => ({
    messages: [...state.messages, message].sort((a, b) => a.createdAt - b.createdAt),
  })),

  clearError: () => set({ error: null }),
}));
